import classNames from "classnames";
import PropTypes from "prop-types";
import React, { Fragment, useState } from "react";
import { ColorVariantType } from "../../../types";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Transition } from "@headlessui/react";
import { useConfig } from "@hooks";

interface AlertProps extends ColorVariantType {
  title?: string;
  dismissable?: boolean;
}

const Alert: React.FC<AlertProps> = ({
  variant = "default",
  title,
  dismissable = true,
  children,
}) => {
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const [show, setShow] = useState<boolean>(true);

  const ALERT_CLASS = {
    base: "tw-border tw-border-l-4 tw-px-4 tw-py-3 tw-rounded tw-relative",
    variant: {
      default: "",
      primary: `${
        mode === "vacanceole"
          ? "tw-bg-v_blue-light tw-border tw-border-v_blue-dark tw-text-v_blue-dark"
          : "tw-bg-primary_corseole-100 tw-border tw-border-primary_corseole-400 tw-text-primary_corseole-700"
      }`,
      secondary:
        "tw-bg-secondary-100 tw-border tw-border-secondary-400 tw-text-secondary-700",
      info: "tw-bg-blue-100 tw-border tw-border-blue-400 tw-text-blue-700",
      success: "tw-bg-green-100 tw-border-green-400 tw-text-green-700",
      warning: "tw-bg-yellow-100 tw-border-yellow-400 tw-text-yellow-700",
      danger: "tw-bg-red-100 tw-border-red-400 tw-text-red-700",
    },
    dismissable: "tw-pr-10",
    title: "tw-font-bold tw-mr-2",
    children: "tw-block sm:tw-inline",
    buttonDismissable:
      "tw-absolute tw-top-0 tw-bottom-0 tw-right-0 tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-in-out",
  };

  return (
    <>
      <Transition
        show={show}
        as={Fragment}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0"
        enterTo="tw-opacity-100"
        leave="tw-transition tw-ease-in tw-duration-150"
        leaveFrom="tw-opacity-100"
        leaveTo="tw-opacity-0"
      >
        <div
          className={classNames(
            ALERT_CLASS.base,
            ALERT_CLASS.variant[variant],
            dismissable && ALERT_CLASS.dismissable
          )}
          role="alert"
        >
          {title && <span className={ALERT_CLASS.title}>{title}</span>}
          <span className={ALERT_CLASS.children}>{children}</span>
          {dismissable && (
            <button
              className={ALERT_CLASS.buttonDismissable}
              onClick={() => setShow(!show)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </Transition>
    </>
  );
};

Alert.propTypes = {
  variant: PropTypes.oneOf([
    "default",
    "primary",
    "success",
    "info",
    "warning",
    "danger",
  ]),
  title: PropTypes.string,
  dismissable: PropTypes.bool,
};

export default Alert;
